import React, { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { api } from "@/lib/api";
import { useAuth } from "@/lib/auth";
import { toast } from "sonner";
import SEO from "@/components/SEO";
import GoogleAuthButton from "@/components/GoogleAuthButton";
import { extractErrorMessage } from "@/lib/apiError";

const C = {
  bgDark: "#000",
  panel: "#0A2826",
  teal: "#0A8B85",
  tealHover: "#066667",
  tealSoft: "#7BD3CE",
  orange: "#F59E0B",
};

const ClinqLogo = ({ withTagline = true }) => (
  <div className="flex items-center gap-3" data-testid="brand-logo-login">
    <span className="relative inline-flex items-baseline font-extrabold tracking-tight text-2xl"
      style={{ fontFamily: "Manrope, sans-serif", letterSpacing: "-0.03em", color: C.teal }}>
      Cl
      <span className="relative inline-block">
        ı
        <span aria-hidden="true" className="absolute left-1/2 -translate-x-1/2 rounded-full clinq-dot-glow"
          style={{ backgroundColor: C.orange, height: "0.5rem", width: "0.5rem", bottom: "1.05em" }}></span>
      </span>
      nq
    </span>
    {withTagline && (
      <div className="hidden sm:flex flex-col leading-none border-l pl-3 border-white/15">
        <span className="text-[9px] uppercase tracking-[0.22em] font-semibold" style={{ color: C.tealSoft }}>Funder</span>
        <span className="text-[9px] uppercase tracking-[0.22em] font-semibold mt-0.5 text-white/55">Readiness</span>
      </div>
    )}
  </div>
);

export default function Login() {
  const navigate = useNavigate();
  const { setUser } = useAuth();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [busy, setBusy] = useState(false);
  const [unverified, setUnverified] = useState(false);
  const [resendBusy, setResendBusy] = useState(false);
  const [resent, setResent] = useState(false);

  const resend = async () => {
    setResendBusy(true);
    try {
      await api.post("/auth/resend-verification", { email });
      setResent(true);
      toast.success("Fresh verification email sent — check your inbox.");
    } catch {
      toast.error("Could not resend. Please try again.");
    } finally {
      setResendBusy(false);
    }
  };

  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    setUnverified(false);
    try {
      const { data } = await api.post("/auth/login", { email, password });
      if (data.token) localStorage.setItem("session_token", data.token);
      setUser(data.user);
      navigate("/dashboard");
    } catch (err) {
      const detail = err.response?.data?.detail;
      if (err.response?.status === 403 && detail?.code === "email_unverified") {
        setUnverified(true);
        return;
      }
      toast.error(extractErrorMessage(err, "Login failed"));
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="min-h-screen flex flex-col text-white"
      style={{ backgroundColor: C.bgDark, fontFamily: "Manrope, sans-serif" }} data-testid="login-page">
      <SEO
        title="Log in"
        description="Log in to Clinq Prep to keep building your pitch deck, financial model or management accounts. Tailored to your business stage and industry."
        path="/login"
        noindex
      />
      {/* Top nav */}
      <header style={{ backgroundColor: "#000" }}>
        <div className="max-w-7xl mx-auto px-6 sm:px-8 py-4 flex items-center justify-between">
          <Link to="/"><ClinqLogo /></Link>
          <Link to="/signup" data-testid="header-signup"
            className="text-sm font-semibold px-4 py-2 rounded-md transition-colors"
            style={{ backgroundColor: C.teal, color: "#fff" }}>Sign Up</Link>
        </div>
      </header>

      {/* Centered card */}
      <main className="flex-1 flex items-center justify-center px-6 py-12">
        <div className="w-full max-w-md">
          <div className="text-center mb-8">
            <div className="inline-flex items-center gap-1.5 text-[11px] uppercase tracking-[0.22em] font-semibold text-white/50">
              <span className="w-1.5 h-1.5 rounded-full" style={{ backgroundColor: C.orange }}></span>
              Welcome back
            </div>
            <h1 className="font-extrabold text-4xl mt-3 tracking-tight" style={{ letterSpacing: "-0.03em" }}>
              Log in to <span style={{ color: C.teal }}>Clinq Prep</span>
            </h1>
            <p className="mt-2 text-sm text-white/60">Pick up where you left off.</p>
          </div>

          <div className="rounded-2xl p-7 border" style={{ backgroundColor: C.panel, borderColor: "rgba(123, 211, 206, 0.15)" }}>
            <GoogleAuthButton label="Continue with Google" testid="login-google-btn" />

            <div className="flex items-center gap-3 my-5" aria-hidden="true">
              <div className="h-px flex-1" style={{ backgroundColor: "rgba(255,255,255,0.08)" }} />
              <span className="text-[10px] uppercase tracking-[0.2em] text-white/40 font-semibold">or</span>
              <div className="h-px flex-1" style={{ backgroundColor: "rgba(255,255,255,0.08)" }} />
            </div>

            <form onSubmit={submit} className="space-y-4">
              {unverified && (
                <div className="rounded-lg p-4 text-left" data-testid="login-unverified-alert"
                  style={{ backgroundColor: "rgba(245,158,11,0.10)", border: "1px solid rgba(245,158,11,0.35)" }}>
                  <div className="text-sm font-semibold" style={{ color: C.orange }}>
                    Verify your email first
                  </div>
                  <p className="mt-1.5 text-[13px] leading-relaxed text-white/70" data-testid="login-unverified-message">
                    Please verify your email address before logging in. Check your inbox for the
                    verification email we sent when you signed up.
                  </p>
                  {resent ? (
                    <p className="mt-3 text-[13px] font-semibold" style={{ color: C.tealSoft }} data-testid="login-resent-note">
                      Fresh verification email sent — check your inbox.
                    </p>
                  ) : (
                    <button type="button" onClick={resend} disabled={resendBusy}
                      data-testid="login-resend-verification-btn"
                      className="mt-3 w-full py-2.5 rounded-md font-semibold text-sm disabled:opacity-60"
                      style={{ backgroundColor: C.orange, color: "#0A1614" }}>
                      {resendBusy ? "Sending…" : "Resend Verification Email"}
                    </button>
                  )}
                </div>
              )}
              <div>
                <label className="text-xs uppercase tracking-widest text-white/50 font-semibold">Email</label>
                <input
                  type="email" value={email} onChange={(e) => setEmail(e.target.value)} required
                  data-testid="login-email-input"
                  placeholder="founder@yourco.com"
                  className="mt-1.5 w-full px-3 py-2.5 rounded-md focus:outline-none focus:border-[#0A8B85] text-sm text-white placeholder:text-white/30"
                  style={{ backgroundColor: "rgba(0,0,0,0.3)", border: "1px solid rgba(255,255,255,0.15)" }}
                />
              </div>
              <div>
                <label className="text-xs uppercase tracking-widest text-white/50 font-semibold">Password</label>
                <input
                  type="password" value={password} onChange={(e) => setPassword(e.target.value)} required minLength={6}
                  data-testid="login-password-input"
                  className="mt-1.5 w-full px-3 py-2.5 rounded-md focus:outline-none focus:border-[#0A8B85] text-sm text-white"
                  style={{ backgroundColor: "rgba(0,0,0,0.3)", border: "1px solid rgba(255,255,255,0.15)" }}
                />
              </div>
              <button
                type="submit" disabled={busy} data-testid="login-submit-btn"
                className="w-full py-3 rounded-md font-semibold text-sm disabled:opacity-60 transition-colors"
                style={{ backgroundColor: C.teal, color: "#fff" }}
                onMouseOver={(e) => !busy && (e.currentTarget.style.backgroundColor = C.tealHover)}
                onMouseOut={(e) => (e.currentTarget.style.backgroundColor = C.teal)}>
                {busy ? "Signing in…" : "Log in"}
              </button>
            </form>
            <div className="mt-4 text-center">
              <Link to="/forgot-password" data-testid="login-forgot-password"
                className="text-xs font-medium hover:underline"
                style={{ color: C.tealSoft }}>
                Forgot your password?
              </Link>
            </div>
          </div>

          <div className="mt-6 text-center text-sm text-white/60">
            New to Clinq?{" "}
            <Link to="/signup" data-testid="login-to-signup" className="font-semibold hover:underline" style={{ color: C.tealSoft }}>
              Create an account
            </Link>
          </div>
        </div>
      </main>
    </div>
  );
}
